/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import {
  find,
  isEmpty,
  pick,
  split,
  compact,
  head,
  get,
  includes,
  some,
  map,
} from 'lodash';
import { combineEpics, Epic, StateObservable } from 'redux-observable';
import { rx, Observable, observableOf, observableEMPTY } from '@shared/rx';
import {
  ListViewService,
  PermissionService,
  SidebarService,
  StateService,
  STATE_KEYS,
  ToolsService,
} from '@shared/services';
import { IAction, IEpicsService } from '@shared/types';
import {
  LIST_VIEW_OPERATION_ERROR,
  LIST_VIEW_OPERATION_SUCCESS,
} from '@text-content/toaster-messages';
import { EMPTY } from 'rxjs';
// import actions
import {
  XprtDataActions,
  UiActions,
  SessionActions,
  APPLICATION_STARTUP_SUCCESS,
} from '../../actions';
import { IAppState } from '@app/store';
import { ROUTER_NAVIGATION_END } from '@store/actions';
import { DASHBOARD_ROUTE } from '@app/routes';

const viewProperties = [
  'filterModel',
  'sortModel',
  'pageSize',
  'quickFilter',
  'selectedColumns',
  'predefinedFilterModel',
  'viewName',
];

@Injectable()
export class UiEpics implements IEpicsService {
  // eslint-disable-next-line max-params
  public constructor(
    public uiActions: UiActions,
    public listViewService: ListViewService,
    public router: Router,
    public permissionService: PermissionService,
    public sidebarService: SidebarService,
    public toolsService: ToolsService,
  ) {}

  public getEpic(): Epic<any, any, any, any> {
    return combineEpics(
      this.showErrorPage.bind(this),
      this.restoreEntityListView.bind(this),
      this.saveUserListView.bind(this),
      this.restoreUserListView.bind(this),
      this.saveCurrentListView.bind(this),
      this.updateDefaultListView.bind(this),
      this.removeUserListView.bind(this),
      this.restoreDefaultListView.bind(this),
      this.updateSidebar.bind(this),
      this.setSidebarItemOpenState.bind(this),
      this.setSidebarSelectedItem.bind(this),
      this.selectSidebarItemOnRouteChange.bind(this),
      this.openRoute.bind(this),
      this.removeDefaultListViews.bind(this),
      this.fetchUserConfig.bind(this),
    );
  }

  public openRoute(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.OPEN_ROUTE_ACTION].includes(action.type),
      ),
      rx.mergeMap(async (action: IAction) => {
        const uri = action.payload.uri;
        const forceReload = get(action, 'payload.options.forceReload', null);
        const state = get(action, 'payload.options.state', {});
        const extras: NavigationExtras = {
          state,
        };

        if (forceReload) {
          const preNavigationSuccessful = await this.router.navigateByUrl('/', {
            ...extras,
            skipLocationChange: true,
          });
          if (!preNavigationSuccessful) {
            return;
          }
        }
        // call the second navigation only in case when navigate to root has been successful
        await this.router.navigateByUrl(uri, extras);
      }),
      rx.mapTo(observableEMPTY),
      rx.switchAll(),
    );
  }

  /**
   * Method for catching SHOW_ERROR_ACTION action type
   * and calling update Location method
   *
   * @param {Observable} action$ - action Observable
   * @returns {Observable<IAction>}
   */

  private showErrorPage(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.SHOW_ERROR_PAGE_ACTION].includes(action.type),
      ),
      rx.mergeMap(() => {
        this.router.navigate(['/error']);

        return observableEMPTY;
      }),
    );
  }

  /**
   * Sets isOpen state of a sidebar item to the provided value
   */
  private setSidebarItemOpenState(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.SET_SIDEBAR_ITEM_OPEN_STATE].includes(action.type),
      ),
      rx.map((action) => {
        const actionItemId: string = action.payload.item.id;
        const isOpen: boolean = action.payload.openStatus;

        const updatedItems = this.sidebarService.mapCurrentItems((item) =>
          item.id === actionItemId ? { ...item, isOpen } : item,
        );

        return UiActions.updateSidebarItemsAction(updatedItems);
      }),
    );
  }

  private selectSidebarItemOnRouteChange(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) => [ROUTER_NAVIGATION_END].includes(action.type)),
      rx.map((action) => {
        // extracts first meaningfull part of the route (e.g. "tax" from "/tax/add" )
        const route = head(compact(split(action.payload.uri, '/')));
        // searched for a suitable sidebar item
        const sidebarItem =
          route &&
          find(this.sidebarService.getCurrentItemsFlattened(), { route });

        return UiActions.selectSidebarItemAction(sidebarItem || null);
      }),
    );
  }

  /**
   * Marks all currently selected sidebar item with isOpen state
   */
  private setSidebarSelectedItem(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.SELECT_SIDEBAR_ITEM].includes(action.type),
      ),
      rx.map((action) => {
        const route = get(action.payload, 'route');
        const topSidebarIds = map(this.sidebarService.getCurrentItems(), 'id');
        const sidebarItems = this.sidebarService.mapCurrentItems((node) => {
          const isOpen =
            // this is an item we are looking for
            (route && node.route === route) ||
            // or some of subItems are opened (selected item is inside this node)
            some(node.subItemsArray, 'isOpen') ||
            // or if its a top level node - keep its current isOpen state
            (topSidebarIds.includes(node.id) && !!node.isOpen);

          return { ...node, isOpen };
        });

        return UiActions.updateSidebarItemsAction(sidebarItems);
      }),
    );
  }

  /**
   * Recalculates "hidden" property for every sidebar item
   */
  private updateSidebar(
    action$: Observable<IAction>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [
          SessionActions.FETCH_PERMISSIONS_SUCCESS,
          XprtDataActions.FETCH_COMPANIES_SUCCESS,
        ].includes(action.type),
      ),
      rx.map(() => {
        const sidebarItems = this.sidebarService.mapCurrentItems((node) => {
          const hasPermissions = this.permissionService.isRoutePermitted(
            node.route,
            node.requiredPermissions,
          );
          const skipVerificationList = [DASHBOARD_ROUTE];
          const showByName = includes(skipVerificationList, node.route);
          const hidden = showByName ? false : !hasPermissions;

          return { ...node, hidden };
        });

        return UiActions.updateSidebarItemsAction(sidebarItems);
      }),
    );
  }

  private restoreEntityListView(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.RESTORE_ENTITY_LIST_VIEW].includes(action.type),
      ),
      rx.mergeMap(async (action: IAction) => {
        try {
          const entityType = action.payload.entity_type;
          const userViews =
            await this.listViewService.getListViewNames(entityType);
          const listConfig =
            await this.listViewService.restoreDefaultListView(entityType);
          // TODO: check
          if (isEmpty(listConfig)) {
            return UiActions.restoreDefaultListViewAction(entityType);
          }
          if (listConfig.predefinedFilterModel) {
            listConfig.filterModel = {
              ...listConfig.filterModel,
              ...listConfig.predefinedFilterModel,
            };
          }
          const data = {
            userViews,
            currentPage: 1,
            entity_type: entityType,
            ...listConfig,
          };

          return UiActions.updateListViewDataAction(data);
        } catch (err) {
          return observableEMPTY;
        }
      }),
    );
  }

  private saveUserListView(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.SAVE_USER_LIST_VIEW].includes(action.type),
      ),
      rx.mergeMap(async (action: IAction) => {
        const state = store.value;
        const entityType = StateService.getUiListEntityType(state);
        const uiListData = StateService.getByPath(state, STATE_KEYS.UI_LIST);
        const viewName = action.payload.viewName;

        try {
          await this.listViewService.saveListView(
            entityType,
            viewName,
            uiListData,
          );
          const userViews =
            await this.listViewService.getListViewNames(entityType);

          return observableOf(
            UiActions.updateListViewDataAction({ userViews, viewName }),
            UiActions.showToasterMessageAction(LIST_VIEW_OPERATION_SUCCESS),
          );
        } catch (err) {
          return observableOf(
            UiActions.showToasterMessageAction(LIST_VIEW_OPERATION_ERROR),
          );
        }
      }),
      rx.switchAll(),
    );
  }

  private restoreUserListView(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.RESTORE_USER_LIST_VIEW].includes(action.type),
      ),
      rx.mergeMap(async (action: IAction) => {
        const entityType = StateService.getUiListEntityType(store.value);
        const viewName = action.payload.viewName;
        try {
          const listView = await this.listViewService.restoreListView(
            entityType,
            viewName,
          );

          return UiActions.updateListViewDataAction(
            pick(listView, viewProperties),
          );
        } catch (err) {
          return UiActions.showToasterMessageAction(LIST_VIEW_OPERATION_ERROR);
        }
      }),
    );
  }

  private restoreDefaultListView(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.RESTORE_DEFAULT_LIST_VIEW].includes(action.type),
      ),
      rx.map((action: IAction) => {
        const entityType = action.payload.entity_type;
        const viewName = action.payload.viewName;
        const listView = this.listViewService.composeDefaultView(entityType);

        return UiActions.updateListViewDataAction({ ...listView, viewName });
      }),
    );
  }

  private saveCurrentListView(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.UPDATE_LIST_VIEW_DATA].includes(action.type),
      ),
      rx.mergeMap(async (action: IAction) => {
        const state = store.value;
        const entityType = get(
          action,
          'payload.entityType',
          StateService.getUiListEntityType(state),
        );
        const uiListData = StateService.getByPath(state, STATE_KEYS.UI_LIST);
        const viewData = pick(uiListData, viewProperties);

        if (!entityType) {
          return observableEMPTY;
        }

        try {
          await this.listViewService.saveDefaultListView(entityType, viewData);
        } finally {
        }

        return observableEMPTY;
      }),
      rx.switchAll(),
    );
  }

  private updateDefaultListView(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.UPDATE_DEFAULT_LIST_VIEW_DATA].includes(action.type),
      ),
      rx.mergeMap(async (action: IAction) => {
        const entityType = get(action, 'payload.entityType');

        if (!entityType) {
          return observableEMPTY;
        }

        const newViewData = pick(action.payload, viewProperties);
        const currentListViewData =
          await this.listViewService.restoreDefaultListView(entityType);

        const viewData = {
          ...currentListViewData,
          ...newViewData,
        };

        try {
          await this.listViewService.saveDefaultListView(entityType, viewData);
        } finally {
        }

        return observableEMPTY;
      }),
      rx.switchAll(),
    );
  }

  private removeUserListView(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.REMOVE_USER_LIST_VIEW].includes(action.type),
      ),
      rx.mergeMap(async (action: IAction) => {
        const entityType = StateService.getUiListEntityType(store.value);
        const viewName = action.payload.viewName;
        await this.listViewService.removeListView(entityType, viewName);
        const userViews =
          await this.listViewService.getListViewNames(entityType);

        return observableOf(
          UiActions.updateListViewDataAction({ userViews }),
          UiActions.showToasterMessageAction(LIST_VIEW_OPERATION_SUCCESS),
        );
      }),
      rx.switchAll(),
      rx.catchError((err) =>
        observableOf(
          UiActions.showToasterMessageAction(LIST_VIEW_OPERATION_ERROR),
        ),
      ),
    );
  }

  private removeDefaultListViews(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [APPLICATION_STARTUP_SUCCESS].includes(action.type),
      ),
      rx.mergeMap(async () => {
        const entityType: string = StateService.getUiListEntityType(
          store.value,
        );
        await this.listViewService.removeDefaultListViews();

        return observableOf(
          ...[
            UiActions.restoreDefaultListViewAction(entityType),
            // Fetch user list-grid configuration for all modules
            UiActions.fetchUserConfigAction(),
          ],
        );
      }),
      rx.switchAll(),
      rx.catchError((err) =>
        observableOf(
          UiActions.showToasterMessageAction(LIST_VIEW_OPERATION_ERROR),
        ),
      ),
    );
  }

  private fetchUserConfig(
    action$: Observable<IAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [UiActions.FETCH_USER_CONFIG].includes(action.type),
      ),
      rx.mergeMap((action: IAction) => {
        return this.toolsService.getUserConfig().pipe(
          rx.map((userConfig) => {
            return UiActions.fetchUserConfigSuccessAction(userConfig);
          }),
          rx.catchError((_err) => EMPTY),
        );
      }),
    );
  }
}
